import React, { useState, useEffect } from "react";
import {
  Rate,
  Avatar,
  Button,
  Modal,
  Input,
  Upload,
  message,
  Empty,
} from "antd";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import axios from "axios";
import { UploadOutlined } from "@ant-design/icons";
import { post } from "../../services/ApiRouting";

const cardStyle = {
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
  padding: "20px 24px",
  maxWidth: "500px",
  width: "100%",
  margin: "0 auto",
};
const headerStyle = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  marginBottom: "8px",
};
const authorStyle = {
  fontWeight: 600,
  fontSize: "1.1rem",
};
const dateStyle = {
  color: "#888",
  fontSize: "0.95rem",
};
const ratingStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "10px",
};
const ratingValueStyle = {
  fontWeight: 500,
  color: "#B59410",
};
const textStyle = {
  fontSize: "1.05rem",
  color: "#333",
  marginTop: "4px",
};

function ReviewCard({ review }) {
  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <Avatar src={review.avatar} size={48} />
        <div>
          <div style={authorStyle}>{review.name}</div>
          <div style={dateStyle}>
            {review.date || review.created_at?.slice(0, 10) || ""}
          </div>
        </div>
      </div>
      <div style={ratingStyle}>
        <Rate disabled defaultValue={review.rating} />
        <span style={ratingValueStyle}>
          {typeof review.rating === "number"
            ? review.rating.toFixed(1)
            : review.rating}
        </span>
      </div>
      <div style={textStyle}>{review.text || review.comment}</div>
    </div>
  );
}

export default function Reviews({ venuesDetailsFetch, VenuesDetails }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    rating: 0,
    text: "",
    avatar: "",
  });
  const [loading, setLoading] = useState(false);

  // API state
  const [reviewError, setReviewError] = useState(null);

  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => {
    setIsModalOpen(false);
    setForm({ name: "", rating: 0, text: "", avatar: "" });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleRatingChange = (value) => {
    setForm({ ...form, rating: value });
  };

  const handleAvatarChange = (info) => {
    if (info.file.status === "done" || info.file.status === "uploading") {
      // For demo, use local preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setForm((prev) => ({ ...prev, avatar: e.target.result }));
      };
      reader.readAsDataURL(info.file.originFileObj);
    }
  };

  // Submit review to API
  const handleSubmit = async () => {
    if (!form.name || !form.rating || !form.text) {
      message.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    setReviewError(null);
    try {
      // If your API supports avatar upload, handle it accordingly.
      // Here, we just send the avatar as a base64 string if present.

      const formData = new FormData();
      formData.append("vendor_id", VenuesDetails.id);
      formData.append("title", form.name);
      formData.append("stars", form.rating);
      formData.append("review", form.text);
      if (form?.avatar[0]?.originFileObj) {
        formData.append("avatar", form?.avatar[0]?.originFileObj);
      }

      const res = await post("/reviews", formData);
      venuesDetailsFetch();
      message.success("Review submitted!");
      handleClose();
    } catch (err) {
      console.log(err);
      setReviewError("Failed to submit review");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="details-section">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 25,
        }}
      >
        <h4 style={{ margin: 0 }}>Reviews of {VenuesDetails?.name}</h4>
        <Button
          type="primary"
          style={{ background: "var(--primaryColor)" }}
          onClick={handleOpen}
        >
          Write Review
        </Button>
      </div>
      {reviewError ? (
        <p style={{ color: "red" }}>{reviewError}</p>
      ) : VenuesDetails?.reviews?.length === 0 ? (
        <Empty description="No reviews yet." />
      ) : (
        <Swiper
          modules={[Navigation]}
          navigation
          spaceBetween={24}
          slidesPerView={1}
          breakpoints={{
            640: { slidesPerView: 1 },
            900: { slidesPerView: 2 },
          }}
          style={{ padding: "10px 0 30px 0", maxWidth: 1100, margin: "0 auto" }}
        >
          {VenuesDetails?.reviews?.map((review, idx) => (
            <SwiperSlide key={review.id || idx}>
              <ReviewCard review={review} />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
      <Modal
        title="Write a Review"
        open={isModalOpen}
        onCancel={handleClose}
        onOk={handleSubmit}
        okText="Submit"
        confirmLoading={loading}
        centered
        styles={{ body: { padding: 24 } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Input
            name="name"
            placeholder="Your Name"
            value={form.name}
            onChange={handleChange}
            size="large"
            style={{ borderRadius: 8 }}
          />
          <div>
            <span style={{ marginRight: 8 }}>Your Rating:</span>
            <Rate value={form.rating} onChange={handleRatingChange} />
          </div>
          <Input.TextArea
            name="text"
            placeholder="Write your review..."
            value={form.text}
            onChange={handleChange}
            rows={4}
            style={{ borderRadius: 8 }}
          />
          <Upload
            name="avatar"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleAvatarChange}
            accept="image/*"
          >
            <Button icon={<UploadOutlined />}>Upload Avatar (optional)</Button>
          </Upload>
          {form.avatar && (
            <Avatar src={form.avatar} size={48} style={{ marginTop: 8 }} />
          )}
        </div>
      </Modal>
    </div>
  );
}
